import { HistoryOutlined } from "@ant-design/icons";
import { Button, Checkbox, Modal, Tag } from "antd";
import classNames from "classnames";
import React from "react";

import { ICarryForwardChoice, ICarryForwardTask, ICostTotals } from "../types";

import { formatNumberToGB } from "@/utils/currency";

const COST_LABELS: Record<keyof ICostTotals, string> = {
  labour: "Labour",
  plant: "Plant",
  material: "Material",
  subcontractor: "Subcontractor"
};

const CARRY_FORWARD_ALL: ICarryForwardChoice = {
  carryForwardTasks: true,
  copyCosts: true
};

const START_FRESH: ICarryForwardChoice = {
  carryForwardTasks: false,
  copyCosts: false
};

const groupByPlotAndStage = (tasks: ICarryForwardTask[]) => {
  const groups = new Map<
    string,
    { plot: string; stage: string; tasks: ICarryForwardTask[] }
  >();
  tasks.forEach((task) => {
    const key = `${task.plot}-${task.stage}`;
    const group = groups.get(key) ?? {
      plot: task.plot,
      stage: task.stage,
      tasks: []
    };
    group.tasks.push(task);
    groups.set(key, group);
  });
  return [...groups.values()].sort(
    (a, b) => Number(a.plot) - Number(b.plot) || a.stage.localeCompare(b.stage)
  );
};

const CarryForwardOption = ({
  checked,
  onChange,
  title,
  description,
  children
}: {
  checked: boolean;
  /** Omitted when this is the only option: the footer buttons are the choice. */
  onChange?: (checked: boolean) => void;
  title: React.ReactNode;
  description: string;
  children: React.ReactNode;
}) => (
  <div
    className={classNames(
      "rounded-lg border border-solid p-3 transition-colors",
      checked ? "border-blue-200 bg-blue-50/40" : "border-gray-200"
    )}
  >
    {onChange ? (
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)}>
        <span className="font-medium text-gray-900">{title}</span>
      </Checkbox>
    ) : (
      <span className="font-medium text-gray-900">{title}</span>
    )}
    <div
      className={classNames("mt-0.5 text-xs text-gray-500", onChange && "ml-6")}
    >
      {description}
    </div>
    <div
      className={classNames(
        "mt-3 transition-opacity",
        onChange && "ml-6",
        !checked && "opacity-50"
      )}
    >
      {children}
    </div>
  </div>
);

/**
 * Asked before creating a new forecast when the previous month has one: what
 * to bring over from it. Each option only appears when there is something to
 * bring, and both start ticked so carrying forward is the default. With only
 * one option there is nothing to tick: it becomes a plain yes/no question.
 */
export const CarryForwardModal = ({
  open,
  previousMonth,
  incompleteTasks,
  costTotals,
  onConfirm,
  onClose
}: {
  open: boolean;
  previousMonth: string;
  incompleteTasks: ICarryForwardTask[];
  /** `null` when the previous forecast has no costs to copy. */
  costTotals: ICostTotals | null;
  onConfirm: (choice: ICarryForwardChoice) => void;
  onClose: () => void;
}) => {
  const [choice, setChoice] =
    React.useState<ICarryForwardChoice>(CARRY_FORWARD_ALL);

  React.useEffect(() => {
    if (open) setChoice(CARRY_FORWARD_ALL);
  }, [open]);

  const taskGroups = React.useMemo(
    () => groupByPlotAndStage(incompleteTasks),
    [incompleteTasks]
  );

  const hasTasks = incompleteTasks.length > 0;
  const isSingleOption = !(hasTasks && costTotals);

  const updateChoice = (update: Partial<ICarryForwardChoice>) =>
    setChoice((prev) => ({ ...prev, ...update }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      width={560}
      title={
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50">
            <HistoryOutlined className="text-lg text-[#167ef9]" />
          </div>
          <div>
            <div className="text-base font-semibold">
              Carry forward from {previousMonth}?
            </div>
            <div className="text-sm font-normal text-[#787878]">
              Choose what to bring into this month&apos;s forecast. You can
              still change everything in the wizard.
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          {isSingleOption ? (
            <>
              <Button onClick={() => onConfirm(START_FRESH)}>
                No, Start Fresh
              </Button>
              <Button
                type="primary"
                onClick={() => onConfirm(CARRY_FORWARD_ALL)}
              >
                Yes, Carry Forward
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" onClick={() => onConfirm(choice)}>
                Create Forecast
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="mt-4 flex flex-col gap-3">
        {hasTasks && (
          <CarryForwardOption
            checked={isSingleOption || choice.carryForwardTasks}
            onChange={
              isSingleOption
                ? undefined
                : (carryForwardTasks) => updateChoice({ carryForwardTasks })
            }
            title={
              <>
                Incomplete tasks{" "}
                <Tag color="orange" className="ml-1">
                  {incompleteTasks.length}
                </Tag>
              </>
            }
            description="Pre-selects their plots and stages and highlights them on the Allocate Tasks step."
          >
            <div className="max-h-48 overflow-y-auto rounded-md border border-solid border-gray-100 bg-white">
              {taskGroups.map((group) => (
                <div
                  key={`${group.plot}-${group.stage}`}
                  className="border-b border-solid border-gray-50 px-3 py-2 last:border-b-0"
                >
                  <div className="mb-1 flex gap-1">
                    <Tag color="blue" className="m-0 text-[11px]">
                      Plot {group.plot}
                    </Tag>
                    <Tag className="m-0 text-[11px]">{group.stage}</Tag>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {group.tasks.map((task) => (
                      <Tag
                        key={task.taskId + task.plot}
                        color="gold"
                        className="m-0 text-[11px]"
                      >
                        {task.taskName}
                        <span className="ml-1 text-[10px] text-gray-500">
                          ({task.status})
                        </span>
                      </Tag>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CarryForwardOption>
        )}

        {costTotals && (
          <CarryForwardOption
            checked={isSingleOption || choice.copyCosts}
            onChange={
              isSingleOption
                ? undefined
                : (copyCosts) => updateChoice({ copyCosts })
            }
            title="Labour, plant, material & subcontractor costs"
            description="Pre-fills the Add Cost step with last month's entries, priced at today's rates."
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(Object.keys(COST_LABELS) as (keyof ICostTotals)[]).map(
                (key) => (
                  <div
                    key={key}
                    className="rounded-md border border-solid border-gray-100 bg-white px-2 py-1.5"
                  >
                    <div className="text-[11px] text-gray-500">
                      {COST_LABELS[key]}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      £{formatNumberToGB(costTotals[key])}
                    </div>
                  </div>
                )
              )}
            </div>
          </CarryForwardOption>
        )}
      </div>
    </Modal>
  );
};
